import { defineComponent as _defineComponent } from 'vue'
import InvoiceSection from '@/views/my-projects/components/invoice/InvoiceSection.vue';
import InvoicePositionsPosition from '@/views/my-projects/components/invoice/positions/InvoicePositionsPosition.vue';
import { computed, ref, watch } from 'vue';
import InvoicePositionsSum from '@/views/my-projects/components/invoice/positions/InvoicePositionsSum.vue';
import BCXButton from '@/components/molecules/BCXButton.vue';
import useCurrency from '@/mixins/useCurrency';
import useInvoice from '@/views/my-projects/composables/useInvoice';
import { cloneDeep, isEqual } from 'lodash';
import {
  ExtendedInvoiceItem, Invoice, InvoiceItem, InvoiceUnitType
} from '@/models/Invoice';
import useUser from '@/mixins/useUser';


export default /*#__PURE__*/_defineComponent({
  __name: 'InvoicePositions',
  setup(__props) {

const {
  componentInvoicePositionsData,
  fetchUpdatePosition,
  invoice,
  canInvoiceBeEdited,
  errorMessagesPositions,
  componentPaymentData,
  unsavedAreas,
  saveUnsavedChanges
} = useInvoice();
const { tUserFallback: t } = useUser('self');
const localPositionsData = ref(cloneDeep(componentInvoicePositionsData.value));
const autoSaveTrigger = ref(0);
const isItEditing = computed({
  get: () => unsavedAreas.value.positionsData.isUnsaved,
  set: (val: boolean) => {
    unsavedAreas.value.positionsData.isUnsaved = val;
  }
});
const isInitialLoad = ref(true);

const { format: currencyFormat } = useCurrency();

function createDisplayedUnit(position: InvoiceItem): string {
  if (['DAYS', 'HOURS'].includes(position.unit)) return position.unit;
  return position.unitDescription;
}

const expandPosition = (position: InvoiceItem): ExtendedInvoiceItem => ({
  ...position,
  netSum: `${currencyFormat((+position.amount * +position.costSingle))} ${t(`invoice.positions.form.currencyAbbreviation.${componentPaymentData.value.currency}`)}`,
  displayedUnit: createDisplayedUnit(position)
});

const sum = computed(() => {
  const totalNet = localPositionsData.value.reduce((acc, cur) => acc + (+cur.amount * +cur.costSingle), 0);
  const isReverseCharge = componentPaymentData.value.reverseCharge;
  const addedVAT = isReverseCharge ? 0 : totalNet * (componentPaymentData.value.taxRate / 100);
  return {
    totalNet,
    addedVAT,
    totalAmount: totalNet + addedVAT,
    currency: invoice.value?.account.currency,
  };
});

function afterProcessingUnit(position: ExtendedInvoiceItem): ExtendedInvoiceItem {
  if (['DAYS', 'HOURS'].includes(position.displayedUnit)) position.unit = position.displayedUnit as InvoiceUnitType;
  else {
    // FIXED and OTHER are also treated as PIECE
    if (position.displayedUnit === 'FIXED') position.unitDescription = `${t('invoice.positions.form.unitOptions.FIXED')}`;
    else if (position.displayedUnit === 'OTHER') position.unitDescription = `${t('invoice.positions.form.unitOptions.OTHER')}`;
    else position.unitDescription = position.displayedUnit;
    position.unit = 'PIECE';
  }
  return position;
}

async function updatePosition(position: ExtendedInvoiceItem) {
  if (isInitialLoad.value) {
    isInitialLoad.value = false;
    return;
  }
  if (!canInvoiceBeEdited.value) return;

  position.amount = +String(position.amount).replace(/\D+/g, '');
  const invoiceItemId = position.id;
  const item = localPositionsData.value.find((localPosition) => localPosition.id === invoiceItemId);
  position = afterProcessingUnit(position);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { netSum, displayedUnit, ...rest } = position;

  // Exit early if item is missing, or if all relevant fields are unchanged
  if (!item) return;
  const hasChanged = position.unit !== item.unit
    || position.description !== item.description
    || position.amount !== item.amount
    || position.costSingle !== item.costSingle
    || position.unitDescription !== item.unitDescription;
  if (!hasChanged) return;

  if (item) {
    Object.assign(item, rest);
  }
  isItEditing.value = true;
}

function correctPositionNumber() {
  localPositionsData.value.forEach((item, index) => {
    item.posNo = index + 1;
  });
}

const onRemovePosition = async (i: number) => {
  if (!canInvoiceBeEdited.value) return;
  isItEditing.value = true;
  localPositionsData.value.splice(i, 1);
  correctPositionNumber();
};

const onAddPosition = async () => {
  if (!canInvoiceBeEdited.value) return;
  isItEditing.value = true;
  const payload: InvoiceItem = {
    unit: 'HOURS',
    description: '',
    amount: 0,
    costSingle: 0,
    posNo: localPositionsData.value.length + 1,
    id: `timeStamp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    unitDescription: ''
  };
  localPositionsData.value.push(payload);
  correctPositionNumber();
};

function deleteTemporaryId() {
  localPositionsData.value.forEach((item) => {
    if (item.id.startsWith('timeStamp-')) item.id = '';
  });
}

const saveCallback = async () => {
  try {
    deleteTemporaryId();
    await fetchUpdatePosition(0, localPositionsData.value)
      .then((invoice: Invoice) => {
        isItEditing.value = false;
        componentInvoicePositionsData.value = invoice.items;
        autoSaveTrigger.value++;
      });
  } finally {
    unsavedAreas.value.positionsData.isProcessing = false;
  }
};

function onCancel() {
  isItEditing.value = false;
  localPositionsData.value = cloneDeep(componentInvoicePositionsData.value);
}

// If reactive data changes, it is updated
watch(
  () => componentInvoicePositionsData.value,
  (newValue: InvoiceItem[]) => {
    if (!isEqual(newValue, localPositionsData.value)) localPositionsData.value = cloneDeep(newValue); // Deep true would have an impact on the UI if you write slowly.
  },
  { deep: true, immediate: true }
);

/**
 * Syncs error message objects with updated invoice positions.
 *
 * Preserves existing error messages for positions with matching IDs.
 * Initializes new error message entries for newly added positions.
 *
 * @param newPositions - The updated list of invoice positions.
 * @param oldPositions - The previous list of invoice positions.
 */
function syncErrorMessages(newPositions: InvoiceItem[], oldPositions: InvoiceItem[]) {
  const errorMessagesTemplate = () => ({});

  if (!oldPositions || oldPositions.length === 0) {
    errorMessagesPositions.value = newPositions.map(() => errorMessagesTemplate());
    return;
  }

  const oldIds = oldPositions.map((pos) => pos.id);

  errorMessagesPositions.value = newPositions.map((pos) => {
    const oldIndex = oldIds.indexOf(pos.id);
    if (oldIndex !== -1) {
      return errorMessagesPositions.value[oldIndex];
    }
    return errorMessagesTemplate();
  });
}

watch(
  () => localPositionsData.value.slice(),
  (newArr: InvoiceItem[], oldArr: InvoiceItem[] = []) => {
    if (newArr.length !== oldArr.length) syncErrorMessages(newArr, oldArr);
  }
);

watch(
  () => saveUnsavedChanges.value,
  (newVal) => {
    if (newVal === 'save' && unsavedAreas.value.positionsData.isUnsaved) {
      unsavedAreas.value.positionsData.isProcessing = true;
      saveCallback();
    } else unsavedAreas.value.positionsData.isProcessing = false;
  }
);

return { __sfc: true,componentInvoicePositionsData, fetchUpdatePosition, invoice, canInvoiceBeEdited, errorMessagesPositions, componentPaymentData, unsavedAreas, saveUnsavedChanges, t, localPositionsData, autoSaveTrigger, isItEditing, isInitialLoad, currencyFormat, createDisplayedUnit, expandPosition, sum, afterProcessingUnit, updatePosition, correctPositionNumber, onRemovePosition, onAddPosition, deleteTemporaryId, saveCallback, onCancel, syncErrorMessages, InvoiceSection, InvoicePositionsPosition, InvoicePositionsSum, BCXButton }
}

})